/* istanbul ignore file */
import { useState } from 'react';
import { Show } from 'framework/show';
import { ShowSection, SectionRow, ShowPageLayout } from 'framework/ui/layout';
import ViewArtifactStoragePageMenu from 'domain/documents/artifactStorage/Show/ViewArtifactStorageFilePageMenu';
import { useArtifactStorageId } from 'domain/documents/artifactStorage/shared/useArtifactStorageId';
import { ArtifactFileTypeLabel, DELETE_FILE, useArtifactStorageService } from 'domain/shared/artifactStorage';
import ArtifactStorageFileHeader from 'domain/documents/artifactStorage/shared/ArtifactStorageFileHeader';
import DownloadArtifactButton from 'domain/documents/artifactStorage/shared/button/DownloadArtifactButton';
import ArtifactStorageBreadcrumbs from 'domain/documents/artifactStorage/shared/ArtifactStorageBreadcrumbs';
import { DateField, EnumField, NumberField, TextField } from 'framework/ui/field';
import MetaInformationField from 'domain/documents/artifactStorage/Show/shared/field/MetaInformationField';
import ArtifactRelationField from 'domain/documents/artifactStorage/Show/shared/field/ArtifactRelationField';
import {
  ArtifactStorageSizeField,
  ArtifactStorageHashTooltip,
  useArtifactStorageUrl,
} from 'domain/shared/artifactStorage';
import { EmployeeLinkField } from 'domain/shared/employee';
import { AttachmentsListField } from 'domain/shared/artifact/field';
import { DeleteButton } from 'framework/ui/ui-builder';

const ViewArtifactStorageFile = () => {
  const id = useArtifactStorageId()!;
  const { getById, deleteById } = useArtifactStorageService();
  const { getListUrl } = useArtifactStorageUrl();
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  const redirectUrl = () => {
    const path = breadcrumbs.slice(0, breadcrumbs.length - 1).map(({ alias }) => alias);
    return path.length ? `${getListUrl()}/${path.join('/').toLowerCase()}` : getListUrl();
  };

  const getData = async () => {
    const response = await getById(id);
    setBreadcrumbs(response.path);
    return response;
  };

  return (
    <Show getDataMethod={() => getData()}>
      <ShowPageLayout
        breadcrumbs={<ArtifactStorageBreadcrumbs breadcrumbs={breadcrumbs} />}
        menu={<ViewArtifactStoragePageMenu />}
        entityHeader={<ArtifactStorageFileHeader />}
        headerButtons={[
          <DownloadArtifactButton source="artifact" />,
          <DeleteButton
            deleteMethod={() => deleteById(id)}
            displayMessage="Do you really want to delete this document?"
            afterDelete={{
              redirectUrl: redirectUrl(),
              successMessage: 'The Artifact Storage File has been successfully deleted.',
              errorMessage: "Can't delete Artifact Storage File",
            }}
            permissionToCheck={DELETE_FILE}
          />,
        ]}
      >
        <ShowSection title="Artifact information">
          <SectionRow>
            <NumberField source="id" label="ID" />
            <EnumField source="metaInfoType" label="Document type" options={ArtifactFileTypeLabel} />
            <TextField
              source="artifact.hash"
              label={
                <>
                  Hash <ArtifactStorageHashTooltip />
                </>
              }
            />
            <ArtifactStorageSizeField source="artifact.size" label="Size" />
          </SectionRow>
          <SectionRow>
            <AttachmentsListField source="artifact.id" label="Document" isDownloadAvailable={false} isOneArtifact />
            <DateField source="createdAt" label="Created at" />
            <EmployeeLinkField nameSource="createdByName" source="createdById" label="Created by" />
          </SectionRow>
        </ShowSection>
        <ShowSection title="Meta information">
          <MetaInformationField source="metaInformation" />
        </ShowSection>
        <ShowSection title="Artifact relations">
          <ArtifactRelationField source="artifactRelation" />
        </ShowSection>
      </ShowPageLayout>
    </Show>
  );
};

export default ViewArtifactStorageFile;
